<template>
  <section class="bg-gray-50 dark:bg-gray-900 bg-fixed">
    <!-- min-h-screen, not h-screen: the co-branding block makes the page taller
         than the viewport, and a fixed height would center the overflow off the
         top edge, clipping the Fleetwire logo out of reach. -->
    <div class="flex flex-col items-center justify-center px-6 py-8 mx-auto md:min-h-screen">
      <a class="flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white" href="/">
        <img alt="logo" class="w-8 h-8 mr-2" src="https://tenant.fleetwire.io/images/logo/fleetwire-logo-new.png">
        Fleetwire
      </a>
      <div v-if="partner" class="flex flex-col items-center mb-6 text-center">
        <v-icon class="mb-2">mdi-plus</v-icon>
        <img v-if="partner.logo" :alt="`${partner.name} logo`" :src="partner.logo" class="max-w-[200px]">
        <span v-else class="text-xl font-semibold text-gray-900 dark:text-white">{{ partner.name }}</span>
        <span class="mt-2 inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-sm text-primary-dark dark:bg-gray-700 dark:text-primary">
          <v-icon class="mr-1" size="small">mdi-gift-outline</v-icon>
          <template v-if="hasExtendedTrial">
            Referred by {{ partner.name }} — enjoy an extended {{ trialDays }}-day free trial
          </template>
          <template v-else>
            Referred by {{ partner.name }}
          </template>
        </span>
      </div>
      <div class="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700">
        <div class="p-6 space-y-3 md:space-y-4 sm:p-8">
          <div
            v-if="hasPendingSignup"
            class="rounded-md border border-primary-200 bg-primary-50 p-3 text-sm text-gray-800 dark:border-primary-700 dark:bg-gray-700 dark:text-gray-100"
          >
            <p class="mb-3">You already started a free trial — continue setup.</p>
            <div class="flex flex-wrap items-center gap-3">
              <v-btn color="primary" size="small" @click="continueSetup">
                Continue setup
              </v-btn>
              <router-link
                :to="{ name: 'login' }"
                class="font-medium text-primary-dark hover:underline dark:text-primary"
              >
                Log in instead
              </router-link>
            </div>
          </div>

          <h1 class="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
            Start a free {{ trialDays }}-day trial
            <span v-if="partner && hasExtendedTrial" class="block text-sm font-normal text-gray-500 dark:text-gray-400">
              Extended from {{ standardTrialDays }} days, courtesy of {{ partner.name }}
            </span>
          </h1>
          <v-form :disabled="form.busy" class="space-y-2 md:space-y-3" @keydown="form.onKeydown($event)" @submit.prevent="register">
            <div>
              <v-text-field
                v-model="form.company_name"
                :autofocus=true
                :error-messages="form.errors.get('company_name')"
                :label="$t('company_name')"
                density="comfortable"
                name="company_name"
                required
                variant="outlined"
              />
            </div>
            <div>
              <v-text-field
                v-model="form.email"
                :error-messages="form.errors.get('email')"
                :label="$t('company_email')"
                :rules="registrationFormRules.email"
                density="comfortable"
                name="email"
                required
                variant="outlined"
              />
            </div>
            <div>
              <v-text-field
                v-model="form.password"
                :error-messages="form.errors.get('password')"
                :label="$t('password')"
                :rules="[registrationFormRules.required, registrationFormRules.min]"
                autocomplete="new-password"
                density="comfortable"
                name="password"
                type="password"
                variant="outlined"
              />
            </div>

            <div
              v-if="showExistingAccountHelp"
              class="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-gray-800 dark:border-amber-700 dark:bg-gray-700 dark:text-gray-100"
            >
              <p class="mb-2">
                Already started signing up? Use the password you chose, or reset it.
              </p>
              <p class="flex flex-wrap gap-x-3 gap-y-1">
                <router-link
                  :to="{ name: 'login', query: { email: form.email } }"
                  class="font-medium text-primary-dark hover:underline dark:text-primary"
                >
                  Log in
                </router-link>
                <router-link
                  :to="{ name: 'password.request', query: { email: form.email } }"
                  class="font-medium text-primary-dark hover:underline dark:text-primary"
                >
                  Forgot password
                </router-link>
              </p>
            </div>

            <div>
              <v-text-field
                v-model="form.referral_source"
                :error-messages="form.errors.get('referral_source')"
                density="comfortable"
                hint="Optional — a friend, Google, YouTube, Reddit, ChatGPT…"
                label="How did you hear about us?"
                name="referral_source"
                persistent-hint
                variant="outlined"
              />
            </div>

            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input id="fw-agree-checkbox" v-model="form.agree" :error-messages="form.errors.get('agree')" aria-describedby="terms" class="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-primary-300 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-600 dark:ring-offset-gray-800" name="agree" required type="checkbox">
              </div>
              <div class="ml-3 text-sm">
                <label class="font-light text-gray-500 dark:text-gray-300" for="fw-agree-checkbox">I accept the
                  <a v-tippy class="font-medium text-primary-dark hover:underline dark:text-primary"
                     content="Opens in a new window"
                     href="https://fleetwire.io/terms?utm_source=register_page&utm_medium=web&utm_campaign=signup&utm_content=terms_link"
                     target="_blank"
                     @click.stop
                  >{{ $t('terms_of_service') }}</a>
                  &
                  <a v-tippy class="font-medium text-primary-dark hover:underline dark:text-primary"
                     content="Opens in a new window"
                     href="https://fleetwire.io/privacy-policy?utm_source=register_page&utm_medium=web&utm_campaign=signup&utm_content=privacy_link"
                     target="_blank"
                     @click.stop
                  >Privacy Policy</a>
                </label>
              </div>
            </div>
            <div ref="turnstileEl" class="mt-3"></div>

            <v-btn id="fw-register-button" :disabled="isButtonDisabled" :loading="form.busy" block class="mt-5" size="large" type="submit">
              {{ $t('register') }}
            </v-btn>

            <p class="mt-5 text-sm font-light text-gray-500 dark:text-gray-400">
              Already have an account?
              <router-link :to="{ name: 'login' }" class="font-medium text-primary-dark hover:underline dark:text-primary">
                <b>{{ $t('login') }} here</b>
              </router-link>
            </p>
          </v-form>
        </div>
      </div>

      <div class="mt-5 text-center">
        <div class="p-1 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 font-medium text-xs text-gray-600 dark:text-gray-300 mb-14 mx-4 line rounded-md">
          It only takes a few seconds, no credit card required!
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from 'vue';
import {trackOpenAIAdsConversions} from '@/utils/openAIAds';
import {useRoute, useRouter} from 'vue-router';
import {useStore} from 'vuex';
import {useAuth} from '@/composables/useAuth';
import Form from 'vform';
import axios from 'axios';

defineOptions({
  name: 'AuthRegister',
});

interface ReferrerPartner {
  uuid: string;
  name: string;
  logo: string | null;
  trial_days: number | null;
}

const route = useRoute();
const router = useRouter();
const store = useStore();
const {registrationFormRules} = useAuth();

const partner = ref<ReferrerPartner | null>(null);
const standardTrialDays = 14;
const trialDays = computed(() => partner.value?.trial_days ?? standardTrialDays);
const hasExtendedTrial = computed(() => trialDays.value > standardTrialDays);

const form = ref(new Form({
  email: '',
  password: '',
  company_name: '',
  partnerUuid: '',
  agree: false,
  referral_source: '',
  attribution: {} as Record<string, string>,
  'cf-turnstile-response': '',
}));

const ATTRIBUTION_STORAGE_KEY = 'fw_attribution';
// `oppref` is the OpenAI Ads click id: the ad lands with ?oppref=... and the
// marketing site is expected to carry it through to /register. Stored verbatim.
const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'oppref'];

const hasPendingSignup = computed(() => {
  const registerId = store.getters['auth/registerId'];
  return typeof registerId === 'string' && registerId.length > 0;
});

const showExistingAccountHelp = computed(() => {
  return form.value.errors.has('email') || form.value.errors.has('company_name');
});

const continueSetup = () => {
  router.push({name: 'company.next-step'}).catch((err) => console.error(err));
};

/**
 * First-touch attribution: UTM params and referrer are stored on first visit
 * and survive navigation, so the values submitted reflect how the visitor
 * originally arrived rather than the last page they touched.
 */
const captureAttribution = (): Record<string, string> => {
  let stored: Record<string, string> = {};
  try {
    stored = JSON.parse(localStorage.getItem(ATTRIBUTION_STORAGE_KEY) || '{}');
  } catch {
    stored = {};
  }

  const current: Record<string, string> = {};
  ATTRIBUTION_KEYS.forEach((key) => {
    const value = route.query?.[key];
    if (typeof value === 'string' && value) {
      current[key] = value.slice(0, 500);
    }
  });

  if (document.referrer && !document.referrer.includes(window.location.hostname)) {
    current.referrer = document.referrer.slice(0, 500);
  }

  // Origin + path only — never the query string.
  current.landing_page = `${window.location.origin}${window.location.pathname}`.slice(0, 500);

  const merged = {...current, ...stored};

  if (Object.keys(merged).length) {
    try {
      localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(merged));
    } catch {
      // Storage unavailable (private mode) — attribution still submits from memory.
    }
  }

  return merged;
};

const REFERRAL_COOKIE = 'fw_ref';

/**
 * Attribution set by an earlier visit — the /r/{slug} link the visitor clicked
 * before browsing, or the marketing site on fleetwire.io. Shared across
 * subdomains, so a click there is still readable here.
 */
const storedReferrerSlug = (): string | undefined => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${REFERRAL_COOKIE}=([^;]*)`));
  const slug = match ? decodeURIComponent(match[1]) : '';

  return /^[A-Za-z0-9._-]{1,64}$/.test(slug) ? slug : undefined;
};

const fetchReferrerPartner = async (slug: string) => {
  try {
    const {data} = await axios.get(`/v1/fleetwire-partners/${slug}`);

    partner.value = data.partner ?? null;
    form.value.partnerUuid = data.partner?.uuid ?? '';

    if (data.partner?.name && !form.value.referral_source) {
      form.value.referral_source = data.partner.name;
    }
  } catch (error) {
    console.error('Failed to load referral partner:', error);
  }
};

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const turnstileEl = ref<HTMLDivElement | null>(null);
let turnstileWidgetId: string | undefined;

const loadTurnstileScript = (): Promise<void> =>
  new Promise((resolve) => {
    if ((window as any).turnstile) return resolve();
    const existing = document.getElementById('cf-turnstile-script') as HTMLScriptElement | null;
    console.dir(existing);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      return;
    }
    const script = document.createElement('script');
    script.id = 'cf-turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    document.head.appendChild(script);
  });

const renderTurnstile = async () => {
  if (!TURNSTILE_SITE_KEY || !turnstileEl.value) return;
  await loadTurnstileScript();
  const turnstile = (window as any).turnstile;
  if (!turnstile) return;
  turnstileWidgetId = turnstile.render(turnstileEl.value, {
    sitekey: TURNSTILE_SITE_KEY,
    callback: (token: string) => {
      (form.value as any)['cf-turnstile-response'] = token;
    },
    'error-callback': () => {
      (form.value as any)['cf-turnstile-response'] = '';
    },
    'expired-callback': () => {
      (form.value as any)['cf-turnstile-response'] = '';
    },
  });
};

const resetTurnstile = () => {
  const turnstile = (window as any).turnstile;
  if (turnstile && turnstileWidgetId !== undefined) {
    turnstile.reset(turnstileWidgetId);
  }
  (form.value as any)['cf-turnstile-response'] = '';
};

const isFormValid = computed(
  () =>
    ['email', 'password', 'company_name'].every((field) => form.value[field]?.length) &&
    form.value.agree &&
    (!TURNSTILE_SITE_KEY || !!(form.value as any)['cf-turnstile-response'])
);

const isButtonDisabled = computed(() => form.value.busy || !isFormValid.value);

onMounted(() => {
  form.value.email = (route.query?.email as string) || '';
  form.value.company_name = (route.query?.company as string) || '';
  form.value.attribution = captureAttribution();

  const referrerSlug = (route.params?.referrer as string | undefined) || storedReferrerSlug();
  if (referrerSlug) {
    fetchReferrerPartner(referrerSlug);
  }

  renderTurnstile();
});

const saveRegisterId = async (id: string) => {
  try {
    await store.dispatch('auth/saveRegisterId', id);
  } catch (error) {
    console.error('Failed to save registration ID:', error);
  }
};

const register = async () => {
  if (!isFormValid.value) return;

  // Register the user.
  try {
    const {data} = await form.value.post('/v1/register');
    await saveRegisterId(data.register_id);

    // The server confirmed the account (and, when it succeeded, the trial) and
    // handed back the event ids it reported through the Conversions API.
    trackOpenAIAdsConversions(data.openai_ads);

    // A registration is a session change. Any token still in the browser belongs
    // to the *previous* account, and RegisterNextStep only logs in via
    // register_id when it sees an unauthenticated visitor — so leaving the old
    // token in place made it complete setup against the old company and write
    // the new company's details there. Clearing it locally (no logout request,
    // which would fail on an already-stale token) puts the register_id path back
    // in charge.
    store.commit('auth/LOGOUT');

    // $gtag.event('Register', { email: form.value.email });
    // Log success and redirect
    console.info('User registered successfully', data);
    router.push({name: 'company.next-step'}).catch((err) => console.error(err));
  } catch (e) {
    console.error('Registration failed:', e);
    resetTurnstile();
  }
};
</script>
